import React, { useEffect, useState } from 'react';
import { AdminLayout } from './AdminDashboard';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Save, RefreshCw, Lock } from 'lucide-react';
import { toast } from 'sonner';
import ImagePositionEditor from '../../components/ImagePositionEditor';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const { getAuthHeader } = useAuth();
  
  const [colorTheme, setColorTheme] = useState({
    primary: '#4a5d4f',
    secondary: '#9b87c4',
    accent: '#d4a574',
    background: '#f9faf9',
    text: '#1f2937',
    buttonPrimary: '#4a5d4f',
    buttonText: '#ffffff'
  });
  
  const [homeHero, setHomeHero] = useState({
    title: '',
    subtitle: '',
    description: '',
    heroImage: '',
    badge: ''
  });
  
  const [bookInfo, setBookInfo] = useState({
    title: '',
    author: '',
    price: 0,
    description: '',
    coverImage: '',
    purchase_link: ''
  });

  const [siteInfo, setSiteInfo] = useState({
    name: '',
    tagline: '',
    phone: '',
    location: '',
    email: '',
    whatsapp: '',
    social_media: {
      facebook: '',
      instagram: '',
      linkedin: '',
      tiktok: '',
      youtube: ''
    }
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/settings`, {
        headers: getAuthHeader()
      });
      
      const settings = response.data;
      const colorThemeSetting = settings.find(s => s.key === 'color_theme');
      const homeHeroSetting = settings.find(s => s.key === 'home_hero');
      const bookSetting = settings.find(s => s.key === 'book_info');
      const siteSetting = settings.find(s => s.key === 'site_info');
      
      if (colorThemeSetting) setColorTheme(colorThemeSetting.value);
      if (homeHeroSetting) setHomeHero(homeHeroSetting.value);
      if (bookSetting) setBookInfo(bookSetting.value);
      if (siteSetting) setSiteInfo(siteSetting.value);
    } catch (error) {
      toast.error('Failed to fetch settings');
    } finally {
      setLoading(false);
    }
  };

  const saveColorTheme = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/admin/settings/color_theme`,
        { value: colorTheme },
        { headers: getAuthHeader() }
      );
      
      // Apply colors immediately
      const root = document.documentElement;
      root.style.setProperty('--theme-primary', colorTheme.primary);
      root.style.setProperty('--theme-secondary', colorTheme.secondary);
      root.style.setProperty('--theme-accent', colorTheme.accent);
      root.style.setProperty('--theme-background', colorTheme.background);
      root.style.setProperty('--theme-text', colorTheme.text);
      root.style.setProperty('--theme-button-primary', colorTheme.buttonPrimary);
      root.style.setProperty('--theme-button-text', colorTheme.buttonText);
      
      // Trigger re-render of dynamic styles by dispatching custom event
      window.dispatchEvent(new CustomEvent('themeUpdate', { detail: colorTheme }));
      
      toast.success('Colors saved! Click "Preview Homepage" to see changes on the live site.');
    } catch (error) {
      toast.error('Failed to update color theme');
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = () => {
    setColorTheme({
      primary: '#4a5d4f',
      secondary: '#9b87c4',
      accent: '#d4a574',
      background: '#f9faf9',
      text: '#1f2937',
      buttonPrimary: '#4a5d4f',
      buttonText: '#ffffff'
    });
  };

  const saveHomeHero = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/admin/settings/home_hero`,
        { value: homeHero },
        { headers: getAuthHeader() }
      );
      toast.success('Homepage content updated successfully');
    } catch (error) {
      toast.error('Failed to update homepage content');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API_URL}/admin/upload-image`, formData, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'multipart/form-data'
        }
      });

      // Update hero image with the uploaded URL
      const newHeroImage = response.data.url;
      const updatedHomeHero = { ...homeHero, heroImage: newHeroImage };
      setHomeHero(updatedHomeHero);
      
      // Automatically save to database
      await axios.put(
        `${API_URL}/admin/settings/home_hero`,
        { value: updatedHomeHero },
        { headers: getAuthHeader() }
      );
      
      toast.success('Image uploaded and saved! Click "Preview Homepage" to see it live.');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const saveBookInfo = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/admin/settings/book_info`,
        { value: bookInfo },
        { headers: getAuthHeader() }
      );
      toast.success('Book information updated successfully');
    } catch (error) {
      toast.error('Failed to update book information');
    } finally {
      setSaving(false);
    }
  };

  const saveSiteInfo = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/admin/settings/site_info`,
        { value: siteInfo },
        { headers: getAuthHeader() }
      );
      toast.success('Site information updated successfully');
    } catch (error) {
      toast.error('Failed to update site information');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <AdminLayout title="Settings"><div>Loading...</div></AdminLayout>;

  return (
    <AdminLayout title="Settings">
      <div className="space-y-8">
        {/* Color Theme Manager */}
        <Card className="border-0 shadow-lg">
          <CardContent className="p-8">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-serif">Color Theme</h3>
              <Button
                onClick={resetToDefaults}
                variant="outline"
                size="sm"
                className="border-sage-300 text-sage-700"
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Reset to Defaults
              </Button>
            </div>
            
            <form onSubmit={saveColorTheme} className="space-y-6">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Primary Color */}
                <div className="space-y-3">
                  <label className="block text-sm font-medium">Primary Color (Sage Green)</label>
                  <div className="flex items-center space-x-3">
                    <input
                      type="color"
                      value={colorTheme.primary}
                      onChange={(e) => setColorTheme({ ...colorTheme, primary: e.target.value })}
                      className="h-12 w-20 rounded-lg border-2 border-gray-300 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={colorTheme.primary}
                      onChange={(e) => setColorTheme({ ...colorTheme, primary: e.target.value })}
                      className="flex-1 px-3 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none"
                      placeholder="#4a5d4f"
                    />
                  </div>
                  <p className="text-xs text-gray-500">Used for main buttons, headers, links</p>
                </div>

                {/* Secondary Color */}
                <div className="space-y-3">
                  <label className="block text-sm font-medium">Secondary Color (Lavender)</label>
                  <div className="flex items-center space-x-3">
                    <input
                      type="color"
                      value={colorTheme.secondary}
                      onChange={(e) => setColorTheme({ ...colorTheme, secondary: e.target.value })}
                      className="h-12 w-20 rounded-lg border-2 border-gray-300 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={colorTheme.secondary}
                      onChange={(e) => setColorTheme({ ...colorTheme, secondary: e.target.value })}
                      className="flex-1 px-3 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none"
                      placeholder="#9b87c4"
                    />
                  </div>
                  <p className="text-xs text-gray-500">Used for accents, icons</p>
                </div>

                {/* Accent Color */}
                <div className="space-y-3">
                  <label className="block text-sm font-medium">Accent Color</label>
                  <div className="flex items-center space-x-3">
                    <input
                      type="color"
                      value={colorTheme.accent}
                      onChange={(e) => setColorTheme({ ...colorTheme, accent: e.target.value })}
                      className="h-12 w-20 rounded-lg border-2 border-gray-300 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={colorTheme.accent}
                      onChange={(e) => setColorTheme({ ...colorTheme, accent: e.target.value })}
                      className="flex-1 px-3 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none"
                      placeholder="#d4a574"
                    />
                  </div>
                  <p className="text-xs text-gray-500">Used for highlights, badges</p>
                </div>

                {/* Background Color */}
                <div className="space-y-3">
                  <label className="block text-sm font-medium">Background Color</label>
                  <div className="flex items-center space-x-3">
                    <input
                      type="color"
                      value={colorTheme.background}
                      onChange={(e) => setColorTheme({ ...colorTheme, background: e.target.value })}
                      className="h-12 w-20 rounded-lg border-2 border-gray-300 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={colorTheme.background}
                      onChange={(e) => setColorTheme({ ...colorTheme, background: e.target.value })}
                      className="flex-1 px-3 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none"
                      placeholder="#f9faf9"
                    />
                  </div>
                  <p className="text-xs text-gray-500">Main page background</p>
                </div>

                {/* Text Color */}
                <div className="space-y-3">
                  <label className="block text-sm font-medium">Text Color</label>
                  <div className="flex items-center space-x-3">
                    <input
                      type="color"
                      value={colorTheme.text}
                      onChange={(e) => setColorTheme({ ...colorTheme, text: e.target.value })}
                      className="h-12 w-20 rounded-lg border-2 border-gray-300 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={colorTheme.text}
                      onChange={(e) => setColorTheme({ ...colorTheme, text: e.target.value })}
                      className="flex-1 px-3 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none"
                      placeholder="#1f2937"
                    />
                  </div>
                  <p className="text-xs text-gray-500">Main text color</p>
                </div>

                {/* Button Color */}
                <div className="space-y-3">
                  <label className="block text-sm font-medium">Button Color</label>
                  <div className="flex items-center space-x-3">
                    <input
                      type="color"
                      value={colorTheme.buttonPrimary}
                      onChange={(e) => setColorTheme({ ...colorTheme, buttonPrimary: e.target.value })}
                      className="h-12 w-20 rounded-lg border-2 border-gray-300 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={colorTheme.buttonPrimary}
                      onChange={(e) => setColorTheme({ ...colorTheme, buttonPrimary: e.target.value })}
                      className="flex-1 px-3 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none"
                      placeholder="#4a5d4f"
                    />
                  </div>
                  <p className="text-xs text-gray-500">Primary button color</p>
                </div>
              </div>

              {/* Color Preview */}
              <div className="mt-8 p-8 rounded-2xl border-2 border-gray-200" style={{ backgroundColor: colorTheme.background }}>
                <h4 className="text-lg font-semibold mb-6" style={{ color: colorTheme.text }}>
                  Live Preview
                </h4>
                <div className="space-y-4">
                  <div className="flex items-center space-x-4">
                    <div 
                      className="h-20 w-20 rounded-xl shadow-lg flex items-center justify-center"
                      style={{ backgroundColor: colorTheme.primary }}
                    >
                      <span className="text-white font-semibold">Primary</span>
                    </div>
                    <div 
                      className="h-20 w-20 rounded-xl shadow-lg flex items-center justify-center"
                      style={{ backgroundColor: colorTheme.secondary }}
                    >
                      <span className="text-white font-semibold text-sm">Secondary</span>
                    </div>
                    <div 
                      className="h-20 w-20 rounded-xl shadow-lg flex items-center justify-center"
                      style={{ backgroundColor: colorTheme.accent }}
                    >
                      <span className="text-white font-semibold">Accent</span>
                    </div>
                  </div>
                  
                  <div className="mt-6 space-y-4">
                    <h5 className="text-2xl font-serif" style={{ color: colorTheme.primary }}>
                      Sample Heading Text
                    </h5>
                    <p style={{ color: colorTheme.text }}>
                      This is how your body text will appear on the website. Make sure there's enough contrast for readability.
                    </p>
                    <button
                      className="px-6 py-3 rounded-xl font-medium shadow-lg transition-all hover:shadow-xl"
                      style={{ 
                        backgroundColor: colorTheme.buttonPrimary,
                        color: colorTheme.buttonText
                      }}
                    >
                      Sample Button
                    </button>
                  </div>

                  <div className="mt-6 p-6 rounded-xl" style={{ backgroundColor: colorTheme.primary + '15' }}>
                    <h6 className="font-semibold mb-2" style={{ color: colorTheme.primary }}>
                      Sample Card
                    </h6>
                    <p className="text-sm" style={{ color: colorTheme.text }}>
                      This shows how cards and sections will look with your chosen colors.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex space-x-4">
                <Button type="submit" disabled={saving} className="bg-sage-700 hover:bg-sage-800 text-white">
                  <Save className="mr-2 h-4 w-4" />
                  {saving ? 'Saving...' : 'Save Color Theme'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Homepage Hero Content */}
        <Card className="border-0 shadow-lg">
          <CardContent className="p-8">
            <h3 className="text-2xl font-serif mb-6">Homepage Hero Section</h3>
            <form onSubmit={saveHomeHero} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Hero Badge Text</label>
                <input
                  type="text"
                  value={homeHero.badge}
                  onChange={(e) => setHomeHero({ ...homeHero, badge: e.target.value })}
                  placeholder="e.g., Explant Surgery Specialist"
                  className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Main Title</label>
                <input
                  type="text"
                  value={homeHero.title}
                  onChange={(e) => setHomeHero({ ...homeHero, title: e.target.value })}
                  placeholder="e.g., Your Journey to"
                  className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Subtitle (highlighted text)</label>
                <input
                  type="text"
                  value={homeHero.subtitle}
                  onChange={(e) => setHomeHero({ ...homeHero, subtitle: e.target.value })}
                  placeholder="e.g., Healing Begins Here"
                  className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <textarea
                  value={homeHero.description}
                  onChange={(e) => setHomeHero({ ...homeHero, description: e.target.value })}
                  rows={3}
                  placeholder="Compassionate support and expert guidance..."
                  className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Hero Image</label>
                <div className="space-y-3">
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp"
                    onChange={handleImageUpload}
                    disabled={uploading}
                    className="w-full px-4 py-3 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-sage-100 file:text-sage-700 hover:file:bg-sage-200 file:cursor-pointer"
                  />
                  {uploading && (
                    <p className="text-sm text-sage-600">Uploading image...</p>
                  )}
                  {homeHero.heroImage && (
                    <div className="mt-4">
                      <p className="text-sm text-gray-600 mb-2">Current Image:</p>
                      <img 
                        src={homeHero.heroImage}
                        alt="Hero Preview" 
                        className="w-48 h-48 object-cover rounded-lg border-2 border-sage-200 shadow-md"
                        onError={(e) => {
                          console.error('Preview image failed to load:', e.target.src);
                        }}
                      />
                      <p className="text-xs text-gray-500 mt-2 break-all">{homeHero.heroImage}</p>
                    </div>
                  )}
                </div>
              </div>

              <Button type="submit" disabled={saving} className="bg-sage-700 hover:bg-sage-800 text-white">
                <Save className="mr-2 h-4 w-4" />
                {saving ? 'Saving...' : 'Save Homepage Content'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Book Information */}
        <Card className="border-0 shadow-lg">
          <CardContent className="p-8">
            <h3 className="text-2xl font-serif mb-6">Book Information</h3>
            <form onSubmit={saveBookInfo} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Title</label>
                  <input
                    type="text"
                    value={bookInfo.title}
                    onChange={(e) => setBookInfo({ ...bookInfo, title: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Author</label>
                  <input
                    type="text"
                    value={bookInfo.author}
                    onChange={(e) => setBookInfo({ ...bookInfo, author: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none"
                  />
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Price (USD)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={bookInfo.price}
                    onChange={(e) => setBookInfo({ ...bookInfo, price: parseFloat(e.target.value) })}
                    className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Purchase Link (Friesen Press)</label>
                  <input
                    type="url"
                    value={bookInfo.purchase_link}
                    onChange={(e) => setBookInfo({ ...bookInfo, purchase_link: e.target.value })}
                    placeholder="https://books.friesenpress.com/..."
                    className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Cover Image</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    setUploading(true);
                    try {
                      const formData = new FormData();
                      formData.append('file', file);
                      const response = await axios.post(`${API_URL}/admin/upload-image`, formData, {
                        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
                      });
                      setBookInfo({ ...bookInfo, coverImage: response.data.url });
                      toast.success('Cover image uploaded!');
                    } catch { toast.error('Failed to upload image'); }
                    finally { setUploading(false); }
                  }}
                  disabled={uploading}
                  data-testid="book-cover-upload"
                  className="w-full px-4 py-3 rounded-lg border-2 border-sage-200 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-sage-100 file:text-sage-700 hover:file:bg-sage-200 file:cursor-pointer"
                />
                <p className="text-xs text-gray-500 mt-1">Or enter URL:</p>
                <input
                  type="url"
                  value={bookInfo.coverImage}
                  onChange={(e) => setBookInfo({ ...bookInfo, coverImage: e.target.value })}
                  placeholder="https://..."
                  className="w-full mt-1 px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none"
                />
                {bookInfo.coverImage && (
                  <div className="mt-4">
                    <ImagePositionEditor
                      src={bookInfo.coverImage}
                      position={bookInfo.coverImagePosition}
                      onPositionChange={(pos) => setBookInfo({ ...bookInfo, coverImagePosition: pos })}
                      aspectRatio="3/4"
                      label="Book Cover Position"
                    />
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <textarea
                  value={bookInfo.description}
                  onChange={(e) => setBookInfo({ ...bookInfo, description: e.target.value })}
                  rows={4}
                  className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none resize-none"
                />
              </div>

              <Button type="submit" disabled={saving} className="bg-sage-700 hover:bg-sage-800 text-white">
                <Save className="mr-2 h-4 w-4" />
                {saving ? 'Saving...' : 'Save Book Info'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Site Information */}
        <Card className="border-0 shadow-lg">
          <CardContent className="p-8">
            <h3 className="text-2xl font-serif mb-6">Site Information</h3>
            <form onSubmit={saveSiteInfo} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Site Name</label>
                  <input
                    type="text"
                    value={siteInfo.name}
                    onChange={(e) => setSiteInfo({ ...siteInfo, name: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Phone</label>
                  <input
                    type="tel"
                    value={siteInfo.phone}
                    onChange={(e) => setSiteInfo({ ...siteInfo, phone: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Email</label>
                  <input
                    type="email"
                    value={siteInfo.email}
                    onChange={(e) => setSiteInfo({ ...siteInfo, email: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Location</label>
                  <input
                    type="text"
                    value={siteInfo.location}
                    onChange={(e) => setSiteInfo({ ...siteInfo, location: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Tagline</label>
                <input
                  type="text"
                  value={siteInfo.tagline}
                  onChange={(e) => setSiteInfo({ ...siteInfo, tagline: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">WhatsApp Link</label>
                <input
                  type="url"
                  value={siteInfo.whatsapp}
                  onChange={(e) => setSiteInfo({ ...siteInfo, whatsapp: e.target.value })}
                  placeholder="https://wa.me/..."
                  className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none"
                />
              </div>

              <div className="space-y-4">
                <h4 className="font-semibold text-gray-900">Social Media Links</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Facebook</label>
                    <input
                      type="url"
                      value={siteInfo.social_media.facebook}
                      onChange={(e) => setSiteInfo({ 
                        ...siteInfo, 
                        social_media: { ...siteInfo.social_media, facebook: e.target.value }
                      })}
                      className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Instagram</label>
                    <input
                      type="url"
                      value={siteInfo.social_media.instagram}
                      onChange={(e) => setSiteInfo({ 
                        ...siteInfo, 
                        social_media: { ...siteInfo.social_media, instagram: e.target.value }
                      })}
                      className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">LinkedIn</label>
                    <input
                      type="url"
                      value={siteInfo.social_media.linkedin}
                      onChange={(e) => setSiteInfo({ 
                        ...siteInfo, 
                        social_media: { ...siteInfo.social_media, linkedin: e.target.value }
                      })}
                      className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">TikTok</label>
                    <input
                      type="url"
                      value={siteInfo.social_media.tiktok}
                      onChange={(e) => setSiteInfo({ 
                        ...siteInfo, 
                        social_media: { ...siteInfo.social_media, tiktok: e.target.value }
                      })}
                      className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">YouTube</label>
                    <input
                      type="url"
                      value={siteInfo.social_media.youtube}
                      onChange={(e) => setSiteInfo({ 
                        ...siteInfo, 
                        social_media: { ...siteInfo.social_media, youtube: e.target.value }
                      })}
                      className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none"
                    />
                  </div>
                </div>
              </div>

              <Button type="submit" disabled={saving} className="bg-sage-700 hover:bg-sage-800 text-white">
                <Save className="mr-2 h-4 w-4" />
                {saving ? 'Saving...' : 'Save Site Info'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Password Change */}
      <PasswordChangeCard getAuthHeader={getAuthHeader} />
    </AdminLayout>
  );
};

const PasswordChangeCard = ({ getAuthHeader }) => {
  const { logout } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return; }
    if (newPassword.length < 12 || new TextEncoder().encode(newPassword).length > 72) { toast.error('Use at least 12 characters and at most 72 UTF-8 bytes'); return; }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/admin/change-password`, { current_password: currentPassword, new_password: newPassword }, { headers: getAuthHeader() });
      toast.success('Password changed. Please sign in again.');
      logout();
      window.location.assign('/admin/login');
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally { setSaving(false); }
  };

  return (
    <Card className="border-0 shadow-lg">
      <CardContent className="p-8">
        <h3 className="text-2xl font-serif mb-6 flex items-center"><Lock className="mr-3 h-6 w-6 text-sage-700" />Change Password</h3>
        <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
          <div>
            <label className="block text-sm font-medium mb-2">Current Password</label>
            <input type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} required data-testid="current-password-input" className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">New Password</label>
            <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} required minLength={12} data-testid="new-password-input" className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Confirm New Password</label>
            <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required data-testid="confirm-password-input" className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none" />
          </div>
          <Button type="submit" disabled={saving} className="bg-sage-700 hover:bg-sage-800 text-white" data-testid="change-password-btn">
            <Lock className="mr-2 h-4 w-4" />{saving ? 'Changing...' : 'Change Password'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default AdminSettings;
